import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { itemsAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  AlertDialog, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Search, Loader2, AlertTriangle, X, CheckCircle2, GitMerge, ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { MergePreviewBody, CategoryRow } from './MergePreviewBody';

/**
 * Manual Item Merge — narrow "detail-page" flow that lets an owner pick two
 * items, choose which is the master, review the same preview as the
 * automatic Duplicate Finder, and merge.
 *
 * All safety guarantees (tenant scoping, chain-merge prevention, additive
 * category transfer, warehouse/vehicle qty combining, reference repointing,
 * soft-tombstone) live in the backend endpoints:
 *   - POST /api/items/merge-preview
 *   - POST /api/items/merge
 * This modal never bypasses them.
 */
export const ManualMergeModal = ({
  open,
  onOpenChange,
  currentItem,       // full ItemResponse for the current page's item
  onMergeSuccess,    // ({ survivingItemId, mergedAwayItemId, currentWasKilled }) => void
}) => {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);   // full ItemResponse
  const [masterChoice, setMasterChoice] = useState(null);    // 'current' | 'selected'

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [merging, setMerging] = useState(false);

  // Reset when the modal opens/closes. We reset on BOTH transitions so a
  // stale `selectedItem` from a prior session can't be paired with a null
  // `currentItem` (Catalogue clears the prop on close) during the transient
  // re-render just after `onOpenChange(false)` — which used to crash
  // `computeWarnings(null, selectedItem)` and abort the merge flow.
  useEffect(() => {
    setQuery('');
    setResults([]);
    setSelectedItem(null);
    setMasterChoice(null);
    setPreviewOpen(false);
    setPreviewLoading(false);
    setPreviewData(null);
    setConfirmOpen(false);
    setMerging(false);
  }, [open]);

  // Debounced typeahead search — reuses the existing `/items/search` endpoint
  // which already filters out merged/archived items server-side.
  useEffect(() => {
    if (!open) return;
    if (!query || query.trim().length < 2) {
      setResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const resp = await itemsAPI.search(query.trim(), 15);
        // Exclude the current item from search results — same item cannot be
        // merged into itself.
        const filtered = (resp.data || []).filter(
          (r) => r.id !== currentItem?.id,
        );
        setResults(filtered);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Search failed');
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query, open, currentItem?.id]);

  const handleSelectItem = async (searchRow) => {
    // Fetch full item detail so the comparison / warnings have all fields
    setSearching(true);
    try {
      const resp = await itemsAPI.get(searchRow.id);
      const full = resp.data;
      // Backend already filters out merged items from search results but
      // guard anyway — the search endpoint is used elsewhere and could evolve.
      if (full.merged_into_item_id) {
        toast.error('That item has already been merged; pick another.');
        return;
      }
      setSelectedItem(full);
      setMasterChoice((prev) => prev || 'current');
      setQuery('');
      setResults([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load item details');
    } finally {
      setSearching(false);
    }
  };

  const warnings = useMemo(
    () => computeWarnings(currentItem, selectedItem),
    [currentItem, selectedItem],
  );

  const masterItem = masterChoice === 'selected' ? selectedItem : currentItem;
  const duplicateItem = masterChoice === 'selected' ? currentItem : selectedItem;
  const canPreview = Boolean(selectedItem && masterChoice && currentItem?.id !== selectedItem?.id);

  const handlePreview = useCallback(async () => {
    if (!canPreview) return;
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const resp = await itemsAPI.mergePreview({
        master_item_id: masterItem.id,
        duplicate_item_ids: [duplicateItem.id],
      });
      setPreviewData(resp.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to build merge preview');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  }, [canPreview, masterItem, duplicateItem]);

  const handleConfirmMerge = () => {
    // Close the preview Dialog BEFORE opening the AlertDialog — same
    // nested-Radix-dialog fix as FindDuplicates.jsx. `previewData` stays in
    // state so `handleExecuteMerge` still has master + duplicate.
    setPreviewOpen(false);
    setConfirmOpen(true);
  };

  const handleExecuteMerge = async () => {
    if (!previewData) return;
    const masterId = previewData.master.item_id;
    const duplicateIds = (previewData.duplicates || []).map((d) => d.item_id);
    if (!masterId || duplicateIds.length === 0) return;
    setMerging(true);
    try {
      await itemsAPI.merge({
        master_item_id: masterId,
        duplicate_item_ids: duplicateIds,
        confirm: true,
      });
      const currentWasKilled = duplicateIds.includes(currentItem?.id);
      toast.success(
        currentWasKilled
          ? `Merged this item into "${masterItem.name}". Opening the surviving item…`
          : `Merged "${duplicateItem.name}" into this item.`,
      );
      setConfirmOpen(false);
      setPreviewOpen(false);
      setPreviewData(null);
      onOpenChange(false);
      if (onMergeSuccess) {
        onMergeSuccess({
          survivingItemId: masterId,
          mergedAwayItemId: duplicateIds[0],
          currentWasKilled,
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  if (!currentItem) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="manual-merge-dialog"
          onInteractOutside={(e) => {
            // Radix's outer <Dialog> DismissableLayer used to treat clicks on
            // our nested <AlertDialog> action button (portalled sibling) as
            // "outside" clicks. It fired onOpenChange(false), the parent
            // cleared currentItem, and the merge never ran. Suppress dismiss
            // whenever the interaction target lives inside our AlertDialog.
            const target = e.detail?.originalEvent?.target || e.target;
            if (target && target.closest && target.closest('[data-testid="manual-merge-final-confirm"]')) {
              e.preventDefault();
            }
          }}
          onPointerDownOutside={(e) => {
            const target = e.detail?.originalEvent?.target || e.target;
            if (target && target.closest && target.closest('[data-testid="manual-merge-final-confirm"]')) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="h-5 w-5 text-slate-700" />
              Merge with another item
            </DialogTitle>
            <DialogDescription>
              Combine this item with another catalogue item. Warehouse quantities,
              vehicle stock, and every reference (POs, jobs, part requests) move onto
              the item you keep. This action is permanent.
            </DialogDescription>
          </DialogHeader>

          {/* Two-column comparison */}
          <div className="grid gap-3 sm:grid-cols-2">
            <ItemCompareCard
              label="This item"
              item={currentItem}
              isMaster={masterChoice === 'current'}
              disableSelect={!selectedItem}
              onKeep={() => setMasterChoice('current')}
              testid="manual-merge-current-item"
              keepBtnTestid="manual-merge-keep-current"
            />
            <div>
              {selectedItem ? (
                <ItemCompareCard
                  label="Other item"
                  item={selectedItem}
                  isMaster={masterChoice === 'selected'}
                  disableSelect={false}
                  onKeep={() => setMasterChoice('selected')}
                  onClear={() => {
                    setSelectedItem(null);
                    setMasterChoice(null);
                  }}
                  testid="manual-merge-selected-item"
                  keepBtnTestid="manual-merge-keep-selected"
                  clearBtnTestid="manual-merge-clear-selected"
                />
              ) : (
                <SearchPanel
                  query={query}
                  setQuery={setQuery}
                  searching={searching}
                  results={results}
                  onPick={handleSelectItem}
                />
              )}
            </div>
          </div>

          {/* Warnings */}
          {selectedItem && warnings.length > 0 && (
            <div
              className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 mt-3"
              data-testid="manual-merge-warnings"
            >
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase mb-1">
                <ShieldAlert className="h-3.5 w-3.5" />
                Heads up
              </div>
              <ul className="list-disc list-inside space-y-1">
                {warnings.map((w) => (
                  <li key={w.key} data-testid={`manual-merge-warning-${w.key}`}>{w.text}</li>
                ))}
              </ul>
              <div className="mt-1.5 text-[11px] text-amber-700">
                These are warnings only. The backend still enforces every hard rule.
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="manual-merge-cancel">
              Cancel
            </Button>
            <Button
              onClick={handlePreview}
              disabled={!canPreview || previewLoading}
              data-testid="manual-merge-preview-btn"
            >
              {previewLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
              Preview merge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview dialog — same body component as FindDuplicates.jsx */}
      <Dialog open={previewOpen} onOpenChange={(o) => { if (!o) { setPreviewOpen(false); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="manual-merge-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-500" />
              Preview merge
            </DialogTitle>
            <DialogDescription>
              Review what changes when this merge runs. Duplicate items become hidden
              tombstones and every reference moves to the master. This action is permanent.
            </DialogDescription>
          </DialogHeader>

          <MergePreviewBody preview={previewData} loading={previewLoading} />

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setPreviewOpen(false)}
              disabled={merging}
              data-testid="manual-merge-preview-cancel"
            >
              Back
            </Button>
            <Button
              onClick={handleConfirmMerge}
              disabled={merging || previewLoading || !previewData}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="manual-merge-preview-confirm"
            >
              {merging ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
              Confirm merge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Final AlertDialog — opened only after the preview Dialog is closed
          (Radix nested-dialog fix). */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="manual-merge-final-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Merge these items?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will merge{' '}
              <span className="font-semibold text-slate-900">&ldquo;{duplicateItem?.name}&rdquo;</span>{' '}
              into{' '}
              <span className="font-semibold text-slate-900">&ldquo;{masterItem?.name}&rdquo;</span>.
              Warehouse quantities, vehicle stock, and every reference move to the master.
              The duplicate becomes a hidden tombstone.
              <span className="block mt-2 font-semibold text-slate-900">
                This action is permanent and cannot be undone.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {/*
              NOTE: We deliberately use plain <Button>s here instead of Radix's
              <AlertDialogCancel> / <AlertDialogAction>. When this AlertDialog
              opens on top of the outer Manual Merge <Dialog>, Radix's action
              primitive's click event is intercepted by the outer Dialog's
              DismissableLayer — the onClick never fires and the merge request
              never leaves the browser (Sasser reported this on 2026-08-09).
              Plain buttons use ordinary React event delegation which works
              correctly across the nested-portal boundary. We close the
              AlertDialog manually via setConfirmOpen(false).
            */}
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={merging}
              data-testid="manual-merge-cancel-final"
            >
              Cancel
            </Button>
            <Button
              onClick={handleExecuteMerge}
              disabled={merging}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="manual-merge-execute-final"
            >
              {merging ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Yes, merge now
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function SearchPanel({ query, setQuery, searching, results, onPick }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <Label className="text-xs font-semibold uppercase text-slate-600">
        Search for the other item
      </Label>
      <div className="relative mt-1.5">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Name, SKU, or manufacturer part #"
          className="pl-8 h-10"
          data-testid="manual-merge-search-input"
          autoFocus
        />
      </div>
      <div className="mt-2 max-h-56 overflow-y-auto">
        {searching && (
          <div className="flex items-center gap-2 py-2 text-xs text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
          </div>
        )}
        {!searching && query.trim().length >= 2 && results.length === 0 && (
          <div className="py-2 text-xs text-slate-500" data-testid="manual-merge-no-results">
            No active items match &ldquo;{query.trim()}&rdquo;. Merged and archived items are hidden.
          </div>
        )}
        {!searching && query.trim().length < 2 && (
          <div className="py-2 text-xs text-slate-500">
            Type at least 2 characters.
          </div>
        )}
        <ul className="divide-y divide-slate-200">
          {results.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                className="w-full text-left px-2 py-2 hover:bg-white rounded transition"
                onClick={() => onPick(r)}
                data-testid={`manual-merge-result-${r.id}`}
              >
                <div className="font-medium text-sm text-slate-900 truncate" title={r.name}>
                  {r.name}
                </div>
                <div className="text-[11px] text-slate-500">
                  {r.sku && <span className="font-mono">{r.sku}</span>}
                  {r.category && <span className="ml-2">· {r.category}</span>}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ItemCompareCard({
  label, item, isMaster, disableSelect, onKeep, onClear,
  testid, keepBtnTestid, clearBtnTestid,
}) {
  return (
    <div
      className={`rounded-lg border-2 p-3 transition ${
        isMaster
          ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200'
          : 'border-slate-200 bg-white'
      }`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[11px] uppercase font-semibold text-slate-500">{label}</div>
        {onClear && (
          <button
            type="button"
            className="text-slate-400 hover:text-slate-700"
            onClick={onClear}
            data-testid={clearBtnTestid}
            title="Choose a different item"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {isMaster && (
        <div className="mb-1.5 inline-flex items-center gap-1 rounded bg-emerald-600 text-white text-[10px] font-semibold uppercase px-1.5 py-0.5">
          <CheckCircle2 className="h-3 w-3" /> Keep this
        </div>
      )}
      <div className="font-medium text-sm text-slate-900 truncate" title={item.name}>
        {item.name}
      </div>
      <div className="mt-0.5 text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
        {item.sku && <span>SKU <span className="font-mono text-slate-700">{item.sku}</span></span>}
        {item.manufacturer_part_number && (
          <span>MPN <span className="font-mono text-slate-700">{item.manufacturer_part_number}</span></span>
        )}
        {item.is_active === false && (
          <Badge variant="outline" className="text-[10px] py-0 px-1.5">Inactive</Badge>
        )}
        {item.archived_at && (
          <Badge variant="outline" className="text-[10px] py-0 px-1.5 bg-slate-100">Archived</Badge>
        )}
      </div>
      <div className="mt-1.5">
        <CategoryRow
          categories={item.category_names}
          testid={`${testid}-cats`}
        />
      </div>
      <div className="mt-3">
        <Button
          size="sm"
          variant={isMaster ? 'default' : 'outline'}
          onClick={onKeep}
          disabled={disableSelect || isMaster}
          data-testid={keepBtnTestid}
          className={isMaster ? 'bg-emerald-600 hover:bg-emerald-700 pointer-events-none' : ''}
        >
          {isMaster ? 'Keeping this item' : 'Keep this one'}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Client-side soft warnings — these do NOT block the merge; only the backend
// can hard-block. Wording matches the audit plan.
// ---------------------------------------------------------------------------

function computeWarnings(current, other) {
  // Guard both sides — during modal close / re-open transitions the parent
  // (Catalogue / EditItemModal) briefly clears `currentItem` while the modal
  // still holds a `selectedItem` in state. Reading `.name` on either null
  // used to throw a React render error that killed the whole handleExecuteMerge
  // flow before it could POST /api/items/merge (Sasser regression, 2026-08).
  if (!current || !other) return [];
  const out = [];

  const norm = (s) => (s || '').toString().trim().toLowerCase();

  if (norm(current.name) !== norm(other.name)) {
    out.push({ key: 'name', text: 'These items have different names. The kept item\'s name will be used going forward.' });
  }
  if (norm(current.sku) !== norm(other.sku)) {
    out.push({ key: 'sku', text: 'These items have different SKUs. Only the kept item\'s SKU will remain on the catalogue.' });
  }
  if (norm(current.manufacturer_part_number) !== norm(other.manufacturer_part_number)) {
    out.push({ key: 'mpn', text: 'These items have different manufacturer part numbers. Only the kept item\'s MPN will remain.' });
  }
  const currentCats = new Set(current.category_names || []);
  const otherCats = new Set(other.category_names || []);
  const currentOnly = [...currentCats].filter((c) => !otherCats.has(c));
  const otherOnly = [...otherCats].filter((c) => !currentCats.has(c));
  if (currentOnly.length || otherOnly.length) {
    out.push({ key: 'categories', text: 'The items have different categories. The kept item will additively gain any missing categories from the merged item.' });
  }
  const currentCost = current.unit_cost != null ? parseFloat(current.unit_cost) : null;
  const otherCost = other.unit_cost != null ? parseFloat(other.unit_cost) : null;
  if (currentCost !== otherCost) {
    out.push({ key: 'unit_cost', text: 'Unit costs differ between the two items. The kept item\'s unit cost is what future reports will use.' });
  }
  const currentBillable = current.billable_price != null ? parseFloat(current.billable_price) : null;
  const otherBillable = other.billable_price != null ? parseFloat(other.billable_price) : null;
  if (currentBillable !== otherBillable) {
    out.push({ key: 'billable_price', text: 'Billable prices differ. The kept item\'s billable price is what invoices will use going forward.' });
  }
  // Different-warehouses guidance — this uses the exact copy from the audit plan.
  out.push({
    key: 'warehouses',
    text: 'These items have stock in different warehouses. After merge, the kept item will retain inventory in each warehouse. This does not move stock between warehouses.',
  });
  if ((current.image_url || current.image_thumb_url) && !(other.image_url || other.image_thumb_url)) {
    out.push({ key: 'image_only_current', text: 'Only this item has an image. If you keep the other item, the image will not carry over.' });
  }
  if (!(current.image_url || current.image_thumb_url) && (other.image_url || other.image_thumb_url)) {
    out.push({ key: 'image_only_other', text: 'Only the other item has an image. If you keep this item, the image will not carry over.' });
  }
  if (current.is_active === false || current.archived_at || other.is_active === false || other.archived_at) {
    out.push({ key: 'inactive_or_archived', text: 'One of the items is inactive or archived. Confirm this is intentional before merging.' });
  }
  return out;
}

export default ManualMergeModal;
